import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { NewsListParams } from "../../../admin-api/news";
import { adminApi } from "../../../api-client";
import type { CreateNewsItem, UpdateNewsItem } from "../../../schemas/news";
import { queryKeys } from "../../query-keys";

export function useNews(params?: NewsListParams) {
	const listParams = { ...params, limit: params?.limit ?? 20, offset: params?.offset ?? 0 };
	return useQuery({
		placeholderData: keepPreviousData,
		queryFn: () => adminApi.news.list(listParams),
		queryKey: queryKeys.news.list(listParams),
	});
}

export function useNewsItem(id: string) {
	return useQuery({
		enabled: !!id,
		queryFn: () => adminApi.news.get(id),
		queryKey: queryKeys.news.detail(id),
	});
}

export function useCreateNews() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateNewsItem) => adminApi.news.create(data),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.news.all,
			}),
	});
}

export function useUpdateNews() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateNewsItem }) =>
			adminApi.news.update(id, data),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.news.all,
			}),
	});
}

export function useDeleteNews() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => adminApi.news.delete(id),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.news.all,
			}),
	});
}

export function usePublishNews() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => adminApi.news.publish(id),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.news.all,
			}),
	});
}

export function useUnpublishNews() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => adminApi.news.unpublish(id),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.news.all,
			}),
	});
}
